import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { SchedulerDetailsConstants } from '../SchedulerDetails.constants';
import { OVERRIDE_TYPE } from '../overrideType.constants';
import { SchedulerDetailsTypes } from '../SchedulerDetails.types';

const getDurationFromTemplate = (obj?: {
  start_time?: string;
  end_time?: string;
  duration?: number;
}) => {
  const { start_time, end_time, duration } = obj || {};
  if (!start_time || !end_time) {
    return duration || SchedulerDetailsConstants.DURATION;
  }
  return moment(end_time).diff(moment(start_time), 'minutes');
};

/**
 * Formats a week range string from weekStart and weekEnd moments
 * Handles formatting for same year, same month, and cross-year cases
 */
const formatWeekRange = (
  weekStart: moment.Moment,
  weekEnd: moment.Moment
): string => {
  if (weekStart.year() === weekEnd.year()) {
    if (weekStart.month() === weekEnd.month()) {
      return `${weekStart.format('MMM D')} – ${weekEnd.format('D, YYYY')}`;
    } else {
      return `${weekStart.format('MMM D')} – ${weekEnd.format('MMM D, YYYY')}`;
    }
  } else {
    return `${weekStart.format('MMM D, YYYY')} – ${weekEnd.format(
      'MMM D, YYYY'
    )}`;
  }
};

/**
 * Normalizes a week range from a single date string (e.g., "Jan 1, 2026" or "Jan 1")
 * Supports both formats with and without year
 */
const normalizeWeekRangeFromDate = (
  startDateStr: string,
  timezone: string
): string => {
  let weekStart: moment.Moment;
  if (startDateStr.includes(',')) {
    weekStart = moment
      .tz(startDateStr, 'MMM D, YYYY', timezone)
      .startOf('week');
  } else {
    weekStart = moment.tz(startDateStr, 'MMM D', timezone).startOf('week');
  }
  const weekEnd = weekStart.clone().endOf('week');
  return formatWeekRange(weekStart, weekEnd);
};

const initializeSelfScheduleTimeSlots = (
  beTimeSlots: SchedulerDetailsTypes.BackendTimeSlot[],
  timezone: string,
  dateFormat: { label: string }
): SchedulerDetailsTypes.SchedulerState => {
  if (!beTimeSlots || !Array.isArray(beTimeSlots) || beTimeSlots.length === 0) {
    return {};
  }

  moment.updateLocale('en', { week: { dow: 0 } });

  const schedulerState: SchedulerDetailsTypes.SchedulerState = {};

  beTimeSlots.forEach((slot) => {
    const slotStart = moment.parseZone(slot.start_time);
    const slotEnd = moment.parseZone(slot.end_time);
    const weekStart = slotStart.clone().startOf('week');
    const weekRange = normalizeWeekRangeFromDate(
      weekStart.format('MMM D, YYYY'),
      timezone
    );

    if (!schedulerState[weekRange]) {
      schedulerState[weekRange] = { isAllChecked: false, days: {} };
      for (let i = 0; i < 7; i++) {
        const date = weekStart.clone().add(i, 'days');
        const dateKey = date.format(dateFormat?.label);
        schedulerState[weekRange].days[dateKey] = {
          isSelected: false,
          slotsGenerated: [],
          customSlots: [],
          removedSlots: [],
        } as any;
      }
    }

    const dateKey = slotStart.format(dateFormat?.label);
    if (schedulerState[weekRange].days[dateKey]) {
      const id = 'custom' + dateKey;
      // const duration = slotEnd.diff(slotStart, 'minutes');
      const feSlot: any = {
        id: `${id}-${slotStart.format('HH:mm')}-${slotEnd.format('HH:mm')}`,
        start: slotStart.toISOString(),
        end: slotEnd.toISOString(),
        category: 'available' as const,
        title: 'Available',
        backgroundColor: 'var(--kula-colors-primary-100)',
        borderColor: 'var(--kula-colors-primary-100)',
        textColor: 'var(--kula-colors-primary-700)',
        editable: true,
        extendedProps: {
          priority: 1,
          id: `custom${uuidv4()}`,
          category: 'available' as const,
          description: 'Available for self-scheduling',
          type: 'free-slot' as const,
        },
      };

      if (slot?.override_type !== undefined) {
        feSlot.override_type = slot.override_type;
      } else {
        feSlot.override_type = OVERRIDE_TYPE.NONE;
      }

      if (slot?.is_custom !== undefined) {
        feSlot.is_custom = slot.is_custom;
      } else {
        feSlot.is_custom = false;
      }
      // Mark as server-loaded so override_type is not re-evaluated in the payload hook
      feSlot._fromServer = true;

      schedulerState[weekRange].days[dateKey].customSlots.push(feSlot);

      // TODO: Commented out is_custom logic - OLD CODE BELOW
      // // Use the simplified is_custom flag approach
      // feSlot.is_custom = slot?.is_custom === true;

      // if (slot?.is_custom === true) {
      //   // Apply custom styling for backend custom slots
      //   feSlot.title = `Custom Slot (${duration} min)`;
      //   // todo: uncomment this after style is updated
      //   // feSlot.backgroundColor = 'var(--kula-colors-success-100)';
      //   // feSlot.borderColor = 'var(--kula-colors-success-300)';
      //   // feSlot.textColor = 'var(--kula-colors-success-300)';
      //   feSlot.classNames = ['custom-slot'];

      //   (schedulerState[weekRange].days[dateKey] as any).customSlots.push(
      //     feSlot
      //   );
      // } else {
      //   // This is a regular generated slot (is_custom: false or undefined)

      //   const day = schedulerState[weekRange].days[dateKey] as any;
      //   if (!day.backendSlots) day.backendSlots = [];
      //   day.backendSlots.push({
      //     id: feSlot.id,
      //     start: feSlot.start,
      //     end: feSlot.end,
      //   });
      // }
    }
  });

  return schedulerState;
};

export const schedulerDetailsUtils = {
  initializeSelfScheduleTimeSlots,
  getDurationFromTemplate,
  normalizeWeekRangeFromDate,
  formatWeekRange,
};
